import axios from 'axios';
import { API_CONFIG, VALIDATION, COMMISSION_RATE } from '../constants/index.js';

/**
 * Donation Service
 * Handles all donation-related API operations
 * Uses the global axios interceptors from AuthService for authentication
 */
class DonationService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.paymentsEndpoint = API_CONFIG.ENDPOINTS.PAYMENTS;
    this.projectsEndpoint = API_CONFIG.ENDPOINTS.PROJECTS;
    this.usersEndpoint = API_CONFIG.ENDPOINTS.USERS;
  }

  /**
   * Create a new donation
   * @param {string} projectId - Project ID
   * @param {number} amount - Donation amount
   * @param {string} memo - Optional memo
   * @returns {Promise<Object>} API response
   */
  async createDonation(projectId, amount, memo = '') {
    try {
      const response = await axios.post(`${this.baseURL}${this.paymentsEndpoint}/donate`, {
        projectId,
        amount,
        memo
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Approve a payment (server-side approval)
   * @param {string} paymentId - Pi payment identifier
   * @param {Object} donationData - Optional donation data for creating record
   * @returns {Promise<Object>} API response
   */
  async approvePayment(paymentId, donationData = null) {
    try {
      const response = await axios.post(`${this.baseURL}${this.paymentsEndpoint}/${paymentId}/approve`, {
        donationData
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Complete a payment (server-side completion)
   * @param {string} paymentId - Pi payment identifier
   * @param {string} txid - Blockchain transaction ID
   * @returns {Promise<Object>} API response
   */
  async completePayment(paymentId, txid) {
    try {
     
      const response = await axios.post(`${this.baseURL}${this.paymentsEndpoint}/${paymentId}/complete`, {
        txid
      });
      
      
      return response.data;
    } catch (error) {
      //console.error('❌ Frontend: Complete payment error');
      //console.error('Error details:', error);
      //console.error('Error response:', error.response?.data);
      //console.error('Error status:', error.response?.status);
      //console.error('Error message:', error.message);
      throw this.handleError(error);
    }
  }

  /**
   * Cancel a payment
   * @param {string} paymentId - Pi payment identifier
   * @returns {Promise<Object>} API response
   */
  async cancelPayment(paymentId) {
    try {
      const response = await axios.post(`${this.baseURL}${this.paymentsEndpoint}/${paymentId}/cancel`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get project donations
   * @param {string} projectId - Project ID
   * @param {Object} options - Query options
   * @returns {Promise<Object>} API response
   */
  async getProjectDonations(projectId, options = {}) {
    try {
      const { page = 1, limit = 20, status = 'completed' } = options;
      const response = await axios.get(`${this.baseURL}${this.projectsEndpoint}/${projectId}/donations`, {
        params: { page, limit, status }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get user's donation history
   * @param {Object} options - Query options
   * @returns {Promise<Object>} API response
   */
  async getUserDonations(options = {}) {
    try {
      const { page = 1, limit = 20 } = options;
      const response = await axios.get(`${this.baseURL}${this.usersEndpoint}/donations`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get donation statistics
   * @returns {Promise<Object>} API response
   */
  async getDonationStats() {
    try {
      const response = await axios.get(`${this.baseURL}${this.paymentsEndpoint}/stats`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Calculate commission breakdown
   * @param {number} amount - Donation amount
   * @returns {Object} Commission breakdown
   */
  calculateCommission(amount) {
    const gross = parseFloat(amount);
    const commission = gross * COMMISSION_RATE;
    const net = gross - commission;
    
    return {
      gross: parseFloat(gross.toFixed(2)),
      net: parseFloat(net.toFixed(2)),
      commission: parseFloat(commission.toFixed(2)),
      commissionRate: COMMISSION_RATE
    };
  }

  /**
   * Validate donation amount
   * @param {number} amount - Donation amount to validate
   * @returns {Object} Validation result
   */
  validateDonationAmount(amount) {
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount)) {
      return { valid: false, error: 'Invalid amount format' };
    }
    
    if (numAmount < VALIDATION.DONATION.MIN_AMOUNT) {
      return { 
        valid: false, 
        error: `Minimum donation amount is π${VALIDATION.DONATION.MIN_AMOUNT}` 
      };
    }
    
    if (numAmount > VALIDATION.DONATION.MAX_AMOUNT) {
      return { 
        valid: false, 
        error: `Maximum donation amount is π${VALIDATION.DONATION.MAX_AMOUNT}` 
      };
    }
    
    return { valid: true };
  }

  /**
   * Format time ago for donations
   * @param {string} dateString - ISO date string
   * @returns {string} Formatted time ago
   */
  formatTimeAgo(dateString) {
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    
    return date.toLocaleDateString();
  }

  /**
   * Format Pi amount for display
   * @param {number} amount - Amount to format
   * @returns {string} Formatted amount
   */
  formatPiAmount(amount) {
    return `π${parseFloat(amount).toFixed(2)}`;
  }

  /**
   * Shorten transaction hash for display
   * @param {string} fullHash - Full transaction hash
   * @returns {string} Shortened hash
   */
  shortenTxHash(fullHash) {
    if (!fullHash || fullHash.length < 8) return fullHash;
    return `${fullHash.substring(0, 4)}...${fullHash.substring(fullHash.length - 3)}`;
  }

  /**
   * Handle Pi payment flow with callbacks
   * @param {Object} paymentData - Payment data for Pi SDK
   * @param {Object} callbacks - Callback functions
   * @param {string} existingPaymentId - Pre-created payment ID from our backend
   * @returns {Promise<void>}
   */
  async handlePiPayment(paymentData, callbacks = {}, existingPaymentId = null) {
    try {
      // Check if Pi SDK is available
      if (!window.Pi) {
        throw new Error('Pi SDK is not available. Please refresh the page.');
      }

      // First, ensure user has payments scope by re-authenticating
      try {
        await window.Pi.authenticate(['username', 'payments', 'wallet_address'], (payment) => {
          console.log('Incomplete payment found during auth:', payment);
          // Handle incomplete payments if any
        });
      } catch (authError) {
        console.error('Pi authentication error:', authError);
        throw new Error('Failed to authenticate with Pi Network for payments. Please try again.');
      }

      // Create payment with Pi SDK
      window.Pi.createPayment(paymentData, {
        onReadyForServerApproval: async (piPaymentId) => {
          try {
            // Use Pi SDK payment ID and pass donation data for record creation
            await this.approvePayment(piPaymentId, existingPaymentId);
            if (callbacks.onApproval) callbacks.onApproval(piPaymentId);
          } catch (error) {
            console.error('Payment approval failed:', error);
            if (callbacks.onError) callbacks.onError(error);
          }
        },
        
        onReadyForServerCompletion: async (piPaymentId, txid) => {
          try {
            // Use Pi SDK payment ID for completion
            const result = await this.completePayment(piPaymentId, txid);
            if (callbacks.onCompletion) callbacks.onCompletion(result);
          } catch (error) {
            console.error('Payment completion failed:', error);
            if (callbacks.onError) callbacks.onError(error);
          }
        },
        
        onCancel: (piPaymentId) => {
          console.log('Payment cancelled:', piPaymentId);
          if (callbacks.onCancel) callbacks.onCancel(piPaymentId);
        },
        
        onError: (error, payment) => {
          console.error('Pi payment error:', error);
          if (callbacks.onError) callbacks.onError(error, payment);
        }
      });
    } catch (error) {
      console.error('Handle Pi payment error:', error);
      if (callbacks.onError) callbacks.onError(error);
    }
  }

  /**
   * Handle API errors consistently
   * @param {object} error - Axios error object
   * @returns {object} Formatted error
   */
  handleError(error) {
    if (error.response) {
      // Server responded with error status
      const { data, status } = error.response;
      return {
        message: data?.error?.message || 'An error occurred',
        code: data?.error?.code || 'UNKNOWN_ERROR',
        status,
        details: data?.error?.details
      };
    } else if (error.request) {
      // Network error
      return {
        message: 'Network error. Please check your connection.',
        code: 'NETWORK_ERROR',
        status: 0
      };
    } else {
      // Other error
      return {
        message: error.message || 'An unexpected error occurred',
        code: 'UNKNOWN_ERROR',
        status: 0
      };
    }
  }
}

// Create and export singleton instance
const donationService = new DonationService();
export default donationService;
